import { UPDATE_ORGANISATION_RESIDENCY_REQUIREMENT } from "@lib/graphql/mutations";
import { ORGANISATION_SETTINGS_QUERY } from "@lib/graphql/queries";
import { useState } from "react";
import { useMutation, useQuery } from "urql";

const REQUIREMENT_OPTIONS: { value: string; label: string; description: string }[] = [
  {
    value: "unrestricted",
    label: "Unrestricted",
    description:
      "All providers and models — OpenAI (GPT-5.4), Anthropic, AWS Bedrock, DigitalOcean. PII redacted at the boundary for all providers.",
  },
  {
    value: "eu",
    label: "EU",
    description:
      "Only EU-resident models are available — Claude Sonnet 4.6 on AWS Bedrock with EU-geography routing (inference never leaves the EU). PII redacted at the boundary.",
  },
  {
    value: "uk",
    label: "UK",
    description:
      "Only UK-resident models are available — AWS Bedrock pinned to eu-west-2 (London): Claude Sonnet 4.6, GPT-OSS 120B. All inference stays in the UK. PII redacted at the boundary.",
  },
];

export function ResidencySettings() {
  const [{ data }, reexecute] = useQuery({ query: ORGANISATION_SETTINGS_QUERY });
  const [, updateResidencyRequirement] = useMutation(UPDATE_ORGANISATION_RESIDENCY_REQUIREMENT);

  const org = data?.currentOrganisation;
  const isAdmin = org?.currentUserRole === "admin";
  const savedRequirement = (org?.residencyRequirement as string) || "unrestricted";

  const [requirement, setRequirement] = useState(savedRequirement);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  if (!dirty && requirement !== savedRequirement) {
    setRequirement(savedRequirement);
  }

  function handleRequirementChange(value: string) {
    setRequirement(value);
    setDirty(true);
    setSuccess(false);
  }

  async function handleSave() {
    setSaving(true);
    setErrors([]);
    setSuccess(false);

    const result = await updateResidencyRequirement({
      input: { residencyRequirement: requirement },
    });
    const mutationErrors = result.data?.updateOrganisationResidencyRequirement?.errors || [];

    if (mutationErrors.length === 0) {
      setDirty(false);
      setSuccess(true);
      // The org's fixed model may have auto-reset to the requirement's
      // default; refetch so the AI settings model picker reflects it.
      reexecute({ requestPolicy: "network-only" });
    } else {
      setErrors(mutationErrors);
    }
    setSaving(false);
  }

  return (
    <div className="mt-6 space-y-8">
      <section>
        <h2 className="text-lg font-semibold text-text-primary">Data Residency Requirement</h2>
        <p className="mt-1 text-sm text-text-muted">
          Declares where your organisation&apos;s AI inference may run. This determines which models
          can be selected in AI settings; tightening it resets the model to the requirement&apos;s
          default. PII is always redacted at the boundary before any data leaves your account.
        </p>

        <div className="mt-3 space-y-2">
          {REQUIREMENT_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-start gap-3 rounded-md border px-3 py-2 ${
                requirement === option.value
                  ? "border-accent bg-accent/5"
                  : "border-input-border bg-card"
              } ${isAdmin ? "cursor-pointer" : "opacity-60"}`}
            >
              <input
                type="radio"
                name="residency_requirement"
                value={option.value}
                checked={requirement === option.value}
                disabled={!isAdmin}
                onChange={(e) => handleRequirementChange(e.target.value)}
                className="mt-0.5 h-4 w-4 text-accent focus:ring-accent"
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-text-primary">{option.label}</span>
                <span className="mt-0.5 text-xs text-text-muted">{option.description}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      {errors.length > 0 && (
        <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
          {errors.map((error) => (
            <p key={error} className="text-sm text-red-700 dark:text-red-400">
              {error}
            </p>
          ))}
        </div>
      )}
      {success && (
        <p className="text-sm text-green-600 dark:text-green-400">Data residency settings saved.</p>
      )}

      {isAdmin && (
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Residency Settings"}
        </button>
      )}
    </div>
  );
}
